import { motion } from "framer-motion";
import { MASAR } from "@/constants/testIds";
import ChapterMark from "@/components/masar/ChapterMark";
import { useLanguage } from "@/i18n/LanguageContext";

const EASE = [0.22, 1, 0.36, 1];

// Section-wrap helper: chapter mark + editorial headline + optional lede
export function SectionHead({ n, chapter, title, kicker, lede, id, testid }) {
  return (
    <section
      id={id}
      data-testid={testid}
      className="relative border-t border-neutral-900 py-24 sm:py-32"
    >
      <div className="max-w-[1400px] mx-auto px-5 sm:px-10">
        <ChapterMark n={n} label={chapter} />
        <div className="mt-8 grid lg:grid-cols-12 gap-10">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, ease: EASE }}
            className="font-serif text-white text-5xl sm:text-6xl lg:text-[5.5rem] leading-[0.95] tracking-[-0.03em] font-light lg:col-span-8"
          >
            {kicker && (
              <span className="block text-[#FF6A1A] font-serif-italic mb-2">
                {kicker}
              </span>
            )}
            {title}
          </motion.h2>
          {lede && (
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.9, delay: 0.1, ease: EASE }}
              className="lg:col-span-4 text-neutral-400 leading-[1.65] pt-2"
            >
              {lede}
            </motion.p>
          )}
        </div>
      </div>
    </section>
  );
}

export default function HowItWorks() {
  const { t } = useLanguage();
  const steps = t("manifesto.steps");
  return (
    <section
      id="how-it-works"
      data-testid={MASAR.sectionHowItWorks}
      className="relative border-t border-neutral-900 py-24 sm:py-32"
    >
      <div className="max-w-[1400px] mx-auto px-5 sm:px-10">
        <ChapterMark n={2} label={t("manifesto.chapter")} />
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: EASE }}
          className="mt-8 font-serif text-white text-5xl sm:text-6xl lg:text-[5.5rem] leading-[0.95] tracking-[-0.03em] font-light max-w-5xl"
        >
          {t("manifesto.titleA")}{" "}
          <span className="font-serif-italic text-[#FF6A1A]">
            {t("manifesto.titleB")}
          </span>{" "}
          {t("manifesto.titleC")}
        </motion.h2>

        <div className="mt-20 sm:mt-24 grid md:grid-cols-3 gap-x-10 gap-y-16 border-t border-neutral-900 pt-16">
          {steps.map((s, i) => (
            <motion.div
              key={s.t}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.8, delay: i * 0.08, ease: EASE }}
              className="relative"
            >
              <div className="font-serif-italic text-[#FF6A1A] text-6xl leading-none">
                {String(i + 1).padStart(2, "0")}
              </div>
              <h3 className="mt-6 font-serif text-3xl sm:text-4xl text-white leading-[1.05] tracking-[-0.02em] font-light">
                {s.t}
              </h3>
              <p className="mt-5 text-neutral-400 leading-[1.65] max-w-sm">
                {s.b}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// Re-export chapter mark for other files
export { ChapterMark };

// Compatibility shim — some sections still import SectionLabel
export function SectionLabel({ index, label }) {
  const n = parseInt(index, 10) || 0;
  return <ChapterMark n={n} label={label} />;
}
